/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useIntl } from 'react-intl'
import { noop } from 'lodash'

import { EuiFlexGroup, EuiFlexItem, EuiStepsHorizontal } from '@elastic/eui'
import type { EuiStepsHorizontalProps } from '@elastic/eui'

import type { ReactElement } from 'react'

type Props = {
  children: ReactElement
  title: ReactElement
  step: number
  setCurrentStep: (step: number) => void
  resetErrors: () => void
}

const getStatus = (
  currentStep: number,
  step: number,
): EuiStepsHorizontalProps['steps'][number]['status'] => {
  if (currentStep === step) {
    return 'current'
  }

  if (currentStep > step) {
    return 'complete'
  }

  return 'disabled'
}

const ProjectCpsLinkContainer: React.FunctionComponent<Props> = ({
  children,
  title,
  step,
  setCurrentStep,
  resetErrors,
}) => {
  const { formatMessage } = useIntl()

  const steps: EuiStepsHorizontalProps['steps'] = [
    {
      title: formatMessage({ id: 'cps-link.step1', defaultMessage: 'Select projects' }),
      status: getStatus(step, 1),
      onClick: () => {
        resetErrors()
        setCurrentStep(1)
      },
    },
    {
      title: formatMessage({ id: 'cps-link.step2', defaultMessage: 'Review and save' }),
      status: getStatus(step, 2),
      onClick: noop,
    },
  ]

  return (
    <EuiFlexGroup direction='column' alignItems='stretch' gutterSize='s'>
      <EuiFlexItem>{title}</EuiFlexItem>
      <EuiStepsHorizontal steps={steps} size='s' />
      <EuiFlexItem>{children}</EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default ProjectCpsLinkContainer
